// Shared client-side cache + batched fetcher for the location business-hours
// labels surfaced as a badge on location result cards, map popovers, and the
// map card list. POSTs a flat `string[]` of reputation IDs to the backend and
// fans the returned labels out to subscribers.
//
// Two consumers feed this service:
//   - <BusinessHoursBadge> (React) — Coveo Global location result templates.
//   - LocationMap (vanilla TS) — its own popover + card list badges.
//
// Both flows are: render placeholder with `badge--loading` and `***`, request
// the id, then swap in the resolved label when the service notifies.

const API_URL = '/api/business-hours/labels';
// Window over which simultaneous mounts can coalesce into a single POST.
const BATCH_WINDOW_MS = 50;

interface ApiResponse {
  success: boolean;
  data: { labels: Record<string, string> };
  errors: unknown[];
}

// Tri-state per id: no entry = pending (not yet resolved); a string = today's label;
// null = resolved with no label (backend omitted it / returned empty, or the fetch
// failed). `null` is terminal -- the badge hides and we never re-request (the dedupe in
// requestBusinessHoursLabels keys off `cache.has`, which is true for both resolved states).
const cache = new Map<string, string | null>();
const pendingIds = new Set<string>();
let pendingTimer: number | null = null;
const listeners = new Set<() => void>();

// `undefined` = still pending (show the loading pulse); `null` = resolved with no label
// (hide the badge); a string = the label to display.
export function getBusinessHoursLabel(id: string): string | null | undefined {
  return cache.get(id);
}

export function requestBusinessHoursLabels(ids: readonly string[]): void {
  let added = false;
  for (const id of ids) {
    if (!id) continue;
    if (cache.has(id)) continue;
    if (pendingIds.has(id)) continue;
    pendingIds.add(id);
    added = true;
  }
  if (!added) return;
  if (pendingTimer != null) return;
  pendingTimer = window.setTimeout(flushPending, BATCH_WINDOW_MS);
}

export function subscribeBusinessHours(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  listeners.forEach((l) => l());
}

async function flushPending(): Promise<void> {
  pendingTimer = null;
  if (pendingIds.size === 0) return;
  const ids = Array.from(pendingIds);
  pendingIds.clear();
  let labels: Record<string, string> = {};
  try {
    labels = await fetchLabels(ids);
  } catch (err) {
    // Resolve-empty on failure (no retry): every requested id falls through to the
    // `null` branch below, so its badge hides instead of pulsing forever.
    console.warn('businessHoursService: fetch failed', err);
  }

  // Every id in this batch was pending (requestBusinessHoursLabels dedupes on `cache.has`),
  // so each one resolves now: to its label, or to `null` when the backend omitted it,
  // returned an empty string, or the fetch failed. `null` is terminal.
  for (const id of ids) {
    const label = labels[id];
    cache.set(id, label && label.length > 0 ? label : null);
  }
  notify();
}

async function fetchLabels(ids: string[]): Promise<Record<string, string>> {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ids),
  });
  if (!response.ok) throw new Error(`businessHoursService: HTTP ${response.status}`);
  const json = (await response.json()) as ApiResponse;
  if (!json.success) throw new Error('businessHoursService: API returned success:false');
  return json.data?.labels ?? {};
}